import { useCallback, useEffect, useMemo, useState } from "react";
import {
    AlertCircle,
    AlertTriangle,
    CalendarDays,
    ChevronDown,
    CheckCircle2,
    ClipboardList,
    Clock3,
    Loader2,
    Pencil,
    Plus,
    Send,
    Trash2,
    Users,
    XCircle,
} from "lucide-react";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";

const emptyForm = {
    title: "",
    description: "",
    priority: "medium",
    dueDate: "",
};

const filters = [
    { key: "all", label: "All Tasks" },
    { key: "pending", label: "Pending" },
    { key: "in-progress", label: "In Progress" },
    { key: "blocked", label: "Blocked" },
    { key: "completed", label: "Completed" },
];

const formatRoleLabel = (role = "") => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const getTaskCacheKey = (employee) => {
    const id = employee?._id || employee?.id;
    return id ? `employee_tasks_${id}` : null;
};

const readTaskCache = (employee) => {
    const cacheKey = getTaskCacheKey(employee);
    if (!cacheKey) return [];

    try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
        if (!cached?.data) return [];
        return Array.isArray(cached.data) ? cached.data : [];
    } catch {
        localStorage.removeItem(cacheKey);
        return [];
    }
};

const readAnyTaskCache = () => {
    try {
        const keys = Object.keys(localStorage).filter((key) => key.startsWith("employee_tasks_"));
        let latest = null;
        keys.forEach((key) => {
            const cached = JSON.parse(localStorage.getItem(key) || "null");
            if (!cached?.data || !Array.isArray(cached.data)) return;
            if (!latest || cached.savedAt > latest.savedAt) latest = cached;
        });
        return latest?.data || [];
    } catch {
        return [];
    }
};

const writeTaskCache = (employee, data) => {
    const cacheKey = getTaskCacheKey(employee);
    if (!cacheKey) return;
    localStorage.setItem(cacheKey, JSON.stringify({ data, savedAt: Date.now() }));
};

const flattenTeamProgressTasks = (progressData) => (
    (progressData?.members || []).flatMap((member) =>
        (member.tasks || []).map((task) => ({
            ...task,
            assignedTo: task.assignedTo || member.employee,
        })),
    )
);

const mergeTaskLists = (...lists) => {
    const merged = [];
    const seen = new Set();

    lists.flat().forEach((task) => {
        if (!task?._id || seen.has(String(task._id))) return;
        seen.add(String(task._id));
        merged.push(task);
    });

    return merged.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
};

const isTaskVisibleToday = (task) => {
    if (!task?.dueDate) return true;
    const dueDate = new Date(task.dueDate);
    if (Number.isNaN(dueDate.getTime())) return true;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    if (task.scheduleType === "weekly" || task.scheduleType === "range") {
        return dueDate >= todayStart && dueDate <= todayEnd;
    }
    // A single task is an assignment, not a daily schedule. Keep it visible
    // from the moment it is assigned, even when its due date is in the future.
    return true;
};

const filterVisibleScheduledTasks = (taskList = []) => taskList.filter(isTaskVisibleToday);

const Tasks = () => {
    const { employee } = useAuth();
    const cachedTasks = useMemo(() => filterVisibleScheduledTasks(readTaskCache(employee)), [employee]);
    const [tasks, setTasks] = useState(() => cachedTasks.length ? cachedTasks : filterVisibleScheduledTasks(readAnyTaskCache()));
    const [statusFilter, setStatusFilter] = useState("all");
    const [memberFilter, setMemberFilter] = useState("all");
    const [expandedTeamFilters, setExpandedTeamFilters] = useState(() => new Set());
    const [teamEmployees, setTeamEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [assignToEntireTeam, setAssignToEntireTeam] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [openTaskId, setOpenTaskId] = useState("");
    const [openStatusMenuId, setOpenStatusMenuId] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [message, setMessage] = useState("");
    const [teamMembersError, setTeamMembersError] = useState("");
    const [assignmentNotice, setAssignmentNotice] = useState(null);

    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        if (cachedTasks.length) setTasks(cachedTasks);
    }, [cachedTasks]);

    const fetchTasks = useCallback(async () => {
        try {
            const res = await api.get("/api/employee/tasks", {
                cache: false,
                params: { limit: 120, _t: Date.now() },
            });
            const nextTasks = filterVisibleScheduledTasks(Array.isArray(res.data) ? res.data : []);

            if (employee?.role === "lead") {
                try {
                    const progressRes = await api.get("/api/employee/tasks/team-progress", {
                        cache: false,
                        params: { _t: Date.now() },
                    });
                    const teamTasks = flattenTeamProgressTasks(progressRes.data);
                    // Leads monitor every task assigned to their teammates,
                    // including upcoming admin-created work.
                    const mergedTasks = mergeTaskLists(nextTasks, teamTasks);
                    setTasks(mergedTasks);
                    writeTaskCache(employee, mergedTasks);
                    return;
                } catch {
                    // The primary task list is still valid if the progress endpoint is unavailable.
                }
            }

            setTasks(nextTasks);
            writeTaskCache(employee, nextTasks);
        } catch (err) {
            console.error("Failed to load tasks:", err);
        }
    }, [employee]);

    const fetchTeamEmployees = useCallback(async () => {
        try {
            const res = await api.get("/api/employee/tasks/team-employees");
            const currentEmployeeId = String(employee?._id || employee?.id || "");
            const members = Array.isArray(res.data) ? res.data : [];
            setTeamEmployees(members.filter((member) => String(member?._id || member?.id || "") !== currentEmployeeId));
            setTeamMembersError("");
        } catch (err) {
            setTeamMembersError("Unable to load team members.");
        }
    }, [employee]);

    useEffect(() => {
        fetchTasks();
        if (employee?.role === "lead") fetchTeamEmployees();
    }, [employee?.role, fetchTasks, fetchTeamEmployees]);

    useEffect(() => {
        const refreshTasks = () => {
            if (!document.hidden) fetchTasks();
        };

        refreshTasks();
        window.addEventListener("focus", refreshTasks);
        document.addEventListener("visibilitychange", refreshTasks);
        const id = window.setInterval(refreshTasks, 15000);

        return () => {
            window.clearInterval(id);
            window.removeEventListener("focus", refreshTasks);
            document.removeEventListener("visibilitychange", refreshTasks);
        };
    }, [fetchTasks]);

    const stats = useMemo(() => ({
        pending: tasks.filter((task) => task.status === "pending").length,
        progress: tasks.filter((task) => task.status === "in-progress").length,
        blocked: tasks.filter((task) => task.status === "blocked").length,
        completed: tasks.filter((task) => task.status === "completed").length,
    }), [tasks]);

    const completionRate = tasks.length ? Math.round((stats.completed / tasks.length) * 100) : 0;

    const priorityStats = useMemo(() => {
        const counts = { high: 0, medium: 0, low: 0, none: 0 };
        tasks.forEach((task) => {
            const priority = task.priority || "none";
            if (counts[priority] === undefined) counts.none += 1;
            else counts[priority] += 1;
        });
        return counts;
    }, [tasks]);

    const upcomingDeadlines = useMemo(() => (
        tasks
            .filter((task) => task.dueDate && task.status !== "completed")
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
            .slice(0, 4)
    ), [tasks]);

    const sortedTeamEmployees = useMemo(() => (
        [...teamEmployees].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "en", { sensitivity: "base" }))
    ), [teamEmployees]);

    const teamMemberGroups = useMemo(() => {
        const groups = new Map();
        sortedTeamEmployees.forEach((member) => {
            const team = String(member?.team || "Unassigned team").trim() || "Unassigned team";
            if (!groups.has(team)) groups.set(team, []);
            groups.get(team).push(member);
        });
        return [...groups.entries()].sort(([left], [right]) => left.localeCompare(right, "en", { sensitivity: "base" }));
    }, [sortedTeamEmployees]);

    const getMemberTaskCount = useCallback((memberId) => (
        tasks.filter((task) => String(task.assignedTo?._id || task.assignedTo) === String(memberId)).length
    ), [tasks]);

    const toggleTeamFilter = (team) => {
        setExpandedTeamFilters((current) => {
            const next = new Set(current);
            if (next.has(team)) next.delete(team);
            else next.add(team);
            return next;
        });
    };

    const visibleTasks = useMemo(() => (
        tasks.filter((task) => {
            const matchesStatus = statusFilter === "all" || task.status === statusFilter;
            const matchesMember = memberFilter === "all"
                || String(task.assignedTo?._id || task.assignedTo) === String(memberFilter);
            return matchesStatus && matchesMember;
        })
    ), [memberFilter, statusFilter, tasks]);

    const handleCheckbox = (id) => {
        setSelectedEmployees((current) =>
            current.includes(id) ? current.filter((employeeId) => employeeId !== id) : [...current, id],
        );
    };

    const toggleTeamSelection = (members) => {
        const teamMemberIds = members.map((member) => String(member._id || member.id)).filter(Boolean);
        setSelectedEmployees((current) => {
            const selected = new Set(current.map(String));
            const allSelected = teamMemberIds.every((id) => selected.has(id));
            teamMemberIds.forEach((id) => {
                if (allSelected) selected.delete(id);
                else selected.add(id);
            });
            return [...selected];
        });
    };

    const closeModal = () => {
        if (submitting) return;
        setModalOpen(false);
        setEditingTask(null);
        setForm(emptyForm);
        setMessage("");
    };

    const openCreateModal = () => {
        setEditingTask(null);
        setForm(emptyForm);
        setSelectedEmployees([]);
        setAssignToEntireTeam(false);
        setMessage("");
        setModalOpen(true);
    };

    const openEditModal = (task) => {
        setEditingTask(task);
        setForm({
            title: task.title || "",
            description: task.description || "",
            priority: task.priority || "medium",
            dueDate: task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : "",
        });
        setMessage("");
        setModalOpen(true);
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        setMessage("");

        if (!editingTask && !assignToEntireTeam && !selectedEmployees.length) {
            setMessage("Select at least one employee or assign to the entire team.");
            return;
        }

        setSubmitting(true);
        try {
            const dueDate = normalizeDateInput(form.dueDate);
            if (editingTask) {
                const res = await api.patch(`/api/employee/tasks/${editingTask._id}`, {
                    ...form,
                    dueDate,
                });
                setTasks((current) => {
                    const nextTasks = current.map((task) => task._id === editingTask._id ? res.data : task);
                    writeTaskCache(employee, nextTasks);
                    return nextTasks;
                });
                setEditingTask(null);
                setForm(emptyForm);
                setModalOpen(false);
                return;
            }
            const res = await api.post("/api/employee/tasks/create", {
                ...form,
                dueDate,
                assignToEntireTeam,
                assignedTo: assignToEntireTeam ? [] : selectedEmployees,
            });
            const createdTasks = Array.isArray(res.data?.tasks) ? res.data.tasks : [];
            if (createdTasks.length) {
                setTasks((current) => {
                    const existingIds = new Set(current.map((task) => task._id));
                    const nextTasks = [...createdTasks.filter((task) => !existingIds.has(task._id)), ...current];
                    writeTaskCache(employee, nextTasks);
                    return nextTasks;
                });
            } else {
                await fetchTasks();
            }
            if (res.data?.emailDelivery?.failed) {
                setAssignmentNotice({ tone: "error", text: "Task assigned, but one or more employee emails could not be delivered." });
            } else {
                setAssignmentNotice({ tone: "success", text: "Task assigned and employee email sent." });
            }
            setForm(emptyForm);
            setSelectedEmployees([]);
            setAssignToEntireTeam(false);
            setModalOpen(false);
        } catch (err) {
            setMessage(getTaskCreateError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteTask = async (task) => {
        if (!task?._id || deletingId) return;
        if (!window.confirm(`Delete "${task.title}"? This cannot be undone.`)) return;

        setDeletingId(task._id);
        setMessage("");
        try {
            await api.delete(`/api/employee/tasks/${task._id}`);
            setTasks((current) => {
                const nextTasks = current.filter((item) => item._id !== task._id);
                writeTaskCache(employee, nextTasks);
                return nextTasks;
            });
            setOpenTaskId("");
        } catch (err) {
            setMessage(err.response?.data?.message || "Failed to delete task.");
        } finally {
            setDeletingId(null);
        }
    };

    const handleStatusChange = async (id, status) => {
        if (updatingId === id) return;

        setUpdatingId(id);
        try {
            const res = await api.patch(`/api/employee/tasks/${id}/status`, { status });
            setTasks((current) => {
                const nextTasks = current.map((task) => (task._id === id ? { ...task, status: res.data.status } : task));
                writeTaskCache(employee, nextTasks);
                return nextTasks;
            });
        } catch (err) {
            setMessage(err.response?.data?.message || "Failed to update task.");
        } finally {
            setUpdatingId(null);
        }
    };

    return (
        <div className="tasks-premium-wrapper tasks-ref-page text-[var(--text-primary)]">
            <main className="tasks-ref-shell">
                <section className="tasks-premium-hero">
                    <div className="tasks-hero-content">
                        <span className="premium-eyebrow">Task Control</span>
                        <h1>{employee?.role === "lead" ? "Team task tracker" : "Work tracker"}</h1>
                        <p>Organize, assign and track tasks across your team.</p>
                        <div className="premium-meta-row mt-4">
                            <span>{employee?.role === "lead" ? "Managed teams" : `${employee?.team || "Team"} team`}</span>
                            <span>{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", weekday: "long" })}</span>
                        </div>
                    </div>
                    <div className="tasks-hero-art" aria-hidden="true">
                        <ClipboardList size={72} />
                    </div>
                    <div className="tasks-hero-rate">
                        <span>Completion Rate</span>
                        <strong>{completionRate}%</strong>
                        <small>{stats.completed} of {tasks.length} tasks completed</small>
                        <i><em style={{ width: `${completionRate}%` }} /></i>
                    </div>
                </section>

                <div className="tasks-kpi-premium-grid mb-6">
                    <TaskStat label="Pending" value={stats.pending} icon={<Clock3 size={20} />} tone="pending" />
                    <TaskStat label="In Progress" value={stats.progress} icon={<Loader2 size={20} />} tone="progress" />
                    <TaskStat label="Blocked" value={stats.blocked} icon={<AlertTriangle size={20} />} tone="blocked" />
                    <TaskStat label="Completed" value={stats.completed} icon={<CheckCircle2 size={20} />} tone="completed" />
                </div>

                <div className={`tasks-premium-layout ${employee?.role === "lead" ? "tasks-lead-layout" : ""}`}>
                    <section className="tasks-premium-board">
                        <div className="tasks-board-header">
                            <div>
                                <h2>{statusFilter === "all" ? "All tasks" : filters.find((item) => item.key === statusFilter)?.label}</h2>
                            </div>
                            {employee?.role === "lead" && (
                                <button type="button" onClick={openCreateModal} className="tasks-new-btn">
                                    <Plus size={18} /> New Task
                                </button>
                            )}
                        </div>

                        {teamMembersError && (
                            <div className="tasks-alert mb-4">
                                <AlertCircle size={17} />
                                {teamMembersError}
                            </div>
                        )}

                        {employee?.role === "lead" && (
                            <div className="tasks-team-filter-panel" aria-label="Filter tasks by managed team and teammate">
                                <button
                                    type="button"
                                    className={`tasks-all-members-button ${memberFilter === "all" ? "active" : ""}`}
                                    onClick={() => setMemberFilter("all")}
                                >
                                    <span>All managed teams</span>
                                    <b>{tasks.length}</b>
                                </button>
                                <div className="tasks-team-filter-list">
                                {teamMemberGroups.map(([team, members]) => {
                                    const expanded = expandedTeamFilters.has(team);
                                    const taskCount = members.reduce((total, member) => total + getMemberTaskCount(member._id), 0);
                                    return (
                                        <section key={team} className={`tasks-team-filter-group ${expanded ? "expanded" : ""}`}>
                                            <button type="button" className="tasks-team-filter-heading" onClick={() => toggleTeamFilter(team)} aria-expanded={expanded}>
                                                <span>
                                                    <strong>{team}</strong>
                                                    <small>{members.length} member{members.length === 1 ? "" : "s"}</small>
                                                </span>
                                                <span className="tasks-team-filter-meta"><b className={taskCount > 0 ? "has-tasks" : ""}>{taskCount}</b><ChevronDown size={17} /></span>
                                            </button>
                                            {expanded && (
                                                <div className="tasks-member-filter tasks-team-members">
                                                    {members.map((member) => {
                                                        const count = getMemberTaskCount(member._id);
                                                        return (
                                                            <button
                                                                key={member._id}
                                                                type="button"
                                                                className={memberFilter === String(member._id) ? "active" : ""}
                                                                onClick={() => setMemberFilter(String(member._id))}
                                                            >
                                                                <span>{member.name}</span>
                                                                <b className={count > 0 ? "has-tasks" : ""}>{count}</b>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </section>
                                    );
                                })}
                                </div>
                            </div>
                        )}

                        <div className="tasks-premium-tabs">
                            {filters.map((filter) => (
                                <button
                                    key={filter.key}
                                    type="button"
                                    className={statusFilter === filter.key ? "active" : ""}
                                    onClick={() => setStatusFilter(filter.key)}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>

                        <div className="tasks-premium-table-wrap">
                            {tasks.length === 0 ? (
                                <EmptyTasks
                                    title="No tasks yet"
                                    text="Tasks assigned to you or your team will appear here."
                                    showCreate={employee?.role === "lead"}
                                    onCreate={openCreateModal}
                                />
                            ) : visibleTasks.length === 0 ? (
                                <EmptyTasks title="No tasks here" text="Choose another status." />
                            ) : (
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Task</th>
                                            {employee?.role === "lead" && <th>Assigned To</th>}
                                            <th>Assigned By</th>
                                            <th>Due date</th>
                                            {employee?.role !== "lead" && <th>Priority</th>}
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleTasks.map((task) => (
                                            <TaskRow
                                                key={task._id}
                                                task={task}
                                                open={openTaskId === task._id}
                                                statusMenuOpen={openStatusMenuId === task._id}
                                                updating={updatingId === task._id}
                                                showAssignee={employee?.role === "lead"}
                                                canChangeStatus={String(task.assignedTo?._id || task.assignedTo) === String(employee?._id || employee?.id)}
                                                canManage={employee?.role === "lead"}
                                                deleting={deletingId === task._id}
                                                onToggle={() => setOpenTaskId((current) => (current === task._id ? "" : task._id))}
                                                onToggleStatusMenu={() =>
                                                    setOpenStatusMenuId((current) => (current === task._id ? "" : task._id))
                                                }
                                                onStatusChange={(status) => {
                                                    setOpenStatusMenuId("");
                                                    handleStatusChange(task._id, status);
                                                }}
                                                onEdit={() => openEditModal(task)}
                                                onDelete={() => handleDeleteTask(task)}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </section>

                    {employee?.role !== "lead" && <aside className="tasks-ref-side">
                        <section className="tasks-side-card">
                            <span className="premium-eyebrow">Overview</span>
                            <h2>Status Overview</h2>
                            <div className="tasks-side-summary">
                                <div className="tasks-donut-inner">
                                    <strong>{tasks.length}</strong>
                                    <span>tasks</span>
                                </div>
                                <div className="tasks-side-filters">
                                    {filters.map((filter) => {
                                        const count = filter.key === "all" ? tasks.length : tasks.filter(t => t.status === filter.key).length;
                                        return (
                                            <button
                                                key={filter.key}
                                                type="button"
                                                className={statusFilter === filter.key ? "active" : ""}
                                                onClick={() => setStatusFilter(filter.key)}
                                            >
                                                <span>{filter.label}</span>
                                                <span>{count}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="tasks-focus-strip">
                                <strong>Today's Focus</strong>
                                <span>{stats.pending} tasks pending</span>
                            </div>
                        </section>

                        <section className="tasks-side-card">
                            <h2>Tasks by Priority</h2>
                            <PriorityRow label="High Priority" count={priorityStats.high} total={tasks.length} tone="high" />
                            <PriorityRow label="Medium Priority" count={priorityStats.medium} total={tasks.length} tone="medium" />
                            <PriorityRow label="Low Priority" count={priorityStats.low} total={tasks.length} tone="low" />
                            <PriorityRow label="No Priority" count={priorityStats.none} total={tasks.length} tone="none" />
                        </section>

                        <section className="tasks-side-card tasks-deadline-card">
                            <div className="tasks-side-card-head">
                                <h2>Upcoming Deadlines</h2>
                                <span>{upcomingDeadlines.length ? "View all" : ""}</span>
                            </div>
                            {upcomingDeadlines.length ? (
                                <div className="tasks-deadline-list">
                                    {upcomingDeadlines.map((task) => (
                                        <div key={task._id}>
                                            <strong>{task.title}</strong>
                                            <span>{formatDate(task.dueDate)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="tasks-deadline-empty">
                                    <CalendarDays size={54} />
                                    <strong>No upcoming deadlines</strong>
                                    <span>You're all clear!</span>
                                </div>
                            )}
                        </section>
                    </aside>}
                </div>

                {modalOpen && employee?.role === "lead" && (
                    <div className="tasks-modal-backdrop" onMouseDown={closeModal}>
                        <section className="tasks-modal" onMouseDown={(event) => event.stopPropagation()}>
                            <header className="tasks-modal-head">
                                <div>
                                    <span className="premium-section-kicker">{editingTask ? "Task management" : "Team assignment"}</span>
                                    <h2>{editingTask ? "Edit task" : "Create task"}</h2>
                                </div>
                                <button type="button" onClick={closeModal} aria-label="Close task form">
                                    <XCircle size={20} />
                                </button>
                            </header>

                            <form className="tasks-form" onSubmit={handleCreate}>
                                {!editingTask && <label className="tasks-team-toggle">
                                    <input
                                        type="checkbox"
                                        checked={assignToEntireTeam}
                                        onChange={(event) => {
                                            setAssignToEntireTeam(event.target.checked);
                                            if (event.target.checked) setSelectedEmployees([]);
                                        }}
                                    />
                                    <span>
                                        <strong>Assign to all managed teams</strong>
                                        <small>Every active member in the teams assigned to you</small>
                                    </span>
                                </label>}

                                {!editingTask && !assignToEntireTeam && (
                                    <div className="tasks-people-picker">
                                        <div className="tasks-picker-title">
                                            <Users size={17} />
                                            <strong>Select teams or employees</strong>
                                        </div>
                                        <div className="tasks-people-teams">
                                            {teamEmployees.length === 0 ? (
                                                <p>No active employees in your team</p>
                                            ) : (
                                                teamMemberGroups.map(([team, members]) => {
                                                    const memberIds = members.map((member) => String(member._id || member.id));
                                                    const allSelected = memberIds.length > 0 && memberIds.every((id) => selectedEmployees.map(String).includes(id));
                                                    return (
                                                        <section key={team} className="tasks-people-team">
                                                            <label className={`tasks-people-team-head ${allSelected ? "selected" : ""}`}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={allSelected}
                                                                    onChange={() => toggleTeamSelection(members)}
                                                                />
                                                                <span>
                                                                    <strong>{team}</strong>
                                                                    <small>{members.length} active member{members.length === 1 ? "" : "s"}</small>
                                                                </span>
                                                            </label>
                                                            <div className="tasks-people-grid">
                                                                {members.map((member) => {
                                                                    const memberId = String(member._id || member.id);
                                                                    return (
                                                                        <label
                                                                            key={memberId}
                                                                            className={selectedEmployees.map(String).includes(memberId) ? "selected" : ""}
                                                                        >
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedEmployees.map(String).includes(memberId)}
                                                                                onChange={() => handleCheckbox(memberId)}
                                                                            />
                                                                            <span>
                                                                                <strong>{member.name}</strong>
                                                                                <small>{formatRoleLabel(member.role)}</small>
                                                                            </span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        </section>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                )}

                                <TaskInput label="Title">
                                    <input
                                        type="text"
                                        required
                                        value={form.title}
                                        onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                                        placeholder="Enter task title"
                                    />
                                </TaskInput>

                                <TaskInput label="Description">
                                    <textarea
                                        required
                                        rows={4}
                                        value={form.description}
                                        onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                                        placeholder="Describe the task..."
                                    />
                                </TaskInput>

                                <div className="tasks-form-grid">
                                    <TaskInput label="Priority">
                                        <select
                                            value={form.priority}
                                            onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}
                                        >
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                        </select>
                                    </TaskInput>
                                    <TaskInput label="Due date">
                                        <input
                                            type="date"
                                            required
                                            min={editingTask ? undefined : today}
                                            value={form.dueDate}
                                            onChange={(event) => setForm((current) => ({ ...current, dueDate: event.target.value }))}
                                        />
                                    </TaskInput>
                                </div>

                                {message && (
                                    <div className="tasks-alert">
                                        <AlertCircle size={17} />
                                        {message}
                                    </div>
                                )}

                                <div className="tasks-submit">
                                    <span>{editingTask ? `Assigned to ${formatPersonName(editingTask.assignedTo)}` : assignToEntireTeam ? "All managed teams" : `${selectedEmployees.length} selected`}</span>
                                    <div className="tasks-submit-actions">
                                        <button type="button" onClick={closeModal} disabled={submitting}>
                                            Cancel
                                        </button>
                                        <button type="submit" disabled={submitting}>
                                            {submitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                                            {submitting ? (editingTask ? "Saving" : "Creating") : (editingTask ? "Save Changes" : "Assign Task")}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </section>
                    </div>
                )}
                {assignmentNotice && (
                    <div className="tasks-notice-backdrop" role="presentation" onMouseDown={() => setAssignmentNotice(null)}>
                        <section className={`tasks-notice-modal ${assignmentNotice.tone}`} role="dialog" aria-modal="true" aria-live="polite" onMouseDown={(event) => event.stopPropagation()}>
                            {assignmentNotice.tone === "success" ? <CheckCircle2 size={34} /> : <AlertCircle size={34} />}
                            <h2>{assignmentNotice.tone === "success" ? "Task assigned" : "Email delivery issue"}</h2>
                            <p>{assignmentNotice.text}</p>
                            <button type="button" onClick={() => setAssignmentNotice(null)}>OK</button>
                        </section>
                    </div>
                )}
            </main>
        </div>
    );
};

const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const isOverdue = (task) => {
    if (!task.dueDate || task.status === "completed") return false;
    return new Date(task.dueDate).toISOString().split("T")[0] < new Date().toISOString().split("T")[0];
};

const formatPersonName = (person, fallback = "-") => {
    if (!person) return fallback;
    if (typeof person === "string") return person;
    return person.name || person.email || fallback;
};

const formatAssignedByName = (task) => {
    if (task.assignedByName) return task.assignedByName;
    if (task.assignedBy && typeof task.assignedBy === "object") return formatPersonName(task.assignedBy);
    if (task.assignedByRole === "admin") return "Admin";
    if (task.assignedByRole === "lead") return "Team lead";
    return "Admin";
};

const formatAssignedDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const normalizeDateInput = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const indianMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (indianMatch) return `${indianMatch[3]}-${indianMatch[2]}-${indianMatch[1]}`;
    return raw;
};

const getTaskCreateError = (error) => {
    const data = error?.response?.data;
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
    if (typeof data === "string" && data.trim()) return data;
    if (error?.code === "ERR_NETWORK") return "Unable to reach the task server. Check that the backend is running on port 5000.";
    return error?.message || "Failed to create task.";
};

const TaskStat = ({ label, value, icon, tone }) => (
    <div className={`tasks-kpi-glass ${tone || ""}`}>
        <span>{icon}</span>
        <div>
            <small>{label}</small>
            <strong>{value}</strong>
            <em>Tasks</em>
        </div>
    </div>
);

const TaskInput = ({ label, children }) => (
    <label className="tasks-input">
        <span>{label}</span>
        {children}
    </label>
);

const EmptyTasks = ({ title, text, showCreate = false, onCreate }) => (
    <div className="tasks-empty">
        <ClipboardList size={34} />
        <strong>{title}</strong>
        <span>{text}</span>
        {showCreate && (
            <button type="button" onClick={onCreate}>
                <Plus size={16} />
                Create New Task
            </button>
        )}
    </div>
);

const PriorityRow = ({ label, count, total, tone }) => {
    const percent = total ? Math.round((count / total) * 100) : 0;

    return (
        <div className={`tasks-priority-row ${tone}`}>
            <span />
            <strong>{label}</strong>
            <i><em style={{ width: `${percent}%` }} /></i>
            <b>{count} ({percent}%)</b>
        </div>
    );
};

const TaskRow = ({
    task,
    statusMenuOpen,
    updating,
    deleting,
    showAssignee,
    canChangeStatus,
    canManage,
    onToggleStatusMenu,
    onStatusChange,
    onEdit,
    onDelete,
}) => {
    const StatusIcon = task.status === "completed" ? CheckCircle2 : task.status === "blocked" ? AlertTriangle : task.status === "in-progress" ? Loader2 : Clock3;
    return (
        <>
            <tr>
                <td data-label="Task">
                    <div className="tasks-table-title tasks-task-content">
                        <strong>{task.title}</strong>
                        <span>{task.description}</span>
                    </div>
                </td>
                {showAssignee && (
                    <td data-label="Assigned To">
                        {formatPersonName(task.assignedTo)}
                    </td>
                )}
                <td data-label="Assigned By" className="tasks-assigned-meta">
                    <strong>{formatAssignedByName(task)}</strong>
                    <span>Assigned on {formatAssignedDate(task.createdAt)}</span>
                </td>
                <td data-label="Due date" className={isOverdue(task) ? "tasks-table-date overdue" : "tasks-table-date"}>
                    {formatDate(task.dueDate)}
                </td>
                {!showAssignee && (
                    <td data-label="Priority">
                        <span className={`tasks-priority ${task.priority || "medium"}`}>
                            {task.priority || "medium"}
                        </span>
                    </td>
                )}
                <td data-label="Status">
                    <span className={`tasks-status ${task.status}`}>
                        <StatusIcon size={13} />
                        {task.status}
                    </span>
                </td>
                <td data-label="Action">
                    <div className="tasks-table-actions">
                        {canChangeStatus && (
                            <div className="tasks-status-menu">
                                <button type="button" disabled={updating} onClick={onToggleStatusMenu}>
                                    {updating ? "Updating..." : "Change status"}
                                </button>
                                {statusMenuOpen && (
                                    <div className="tasks-status-options">
                                        {filters.filter((item) => item.key !== "all").map((item) => (
                                            <button key={item.key} type="button" className={task.status === item.key ? "active" : ""} onClick={() => onStatusChange(item.key)}>
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {canManage && (
                            <>
                                <button type="button" className="tasks-table-edit" onClick={onEdit}>
                                    <Pencil size={13} /> Edit
                                </button>
                                <button type="button" className="tasks-table-delete" onClick={onDelete} disabled={deleting}>
                                    {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                                    {deleting ? "Deleting" : "Delete"}
                                </button>
                            </>
                        )}
                    </div>
                </td>
            </tr>
        </>
    );
};

export default Tasks;
